import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Download,
  Copy,
  Check,
  CalendarDays,
  Syringe,
  Clock,
  Info,
} from "lucide-react";
import { format, addDays, addWeeks } from "date-fns";
import { es, ptBR, enUS } from "date-fns/locale";
import { PageNavigation, PageBreadcrumb } from "@/components/PageNavigation";

// Get date-fns locale based on i18n language
const getDateLocale = (lang) => {
  const locales = {
    en: enUS,
    es: es,
    pt: ptBR,
  };
  return locales[lang] || enUS;
};

// Peptide options for the planner - use translation keys
const PEPTIDE_OPTIONS = [
  { value: "retatrutide", nameKey: "retatrutide", unit: "mg" },
  { value: "tirzepatide", nameKey: "tirzepatide", unit: "mg" },
  { value: "ipamorelin", nameKey: "ipamorelin", unit: "mcg" },
  { value: "tesamorelin", nameKey: "tesamorelin", unit: "mg" },
  { value: "nad", nameKey: "nad", unit: "mg" },
  { value: "klow", nameKey: "klow", unit: "mg" },
  { value: "glow", nameKey: "glow", unit: "mg" },
  { value: "ss31", nameKey: "ss31", unit: "mg" },
  { value: "motsc", nameKey: "motsc", unit: "mg" },
  { value: "tb500", nameKey: "tb500", unit: "mg" },
  { value: "ghkcu", nameKey: "ghkcu", unit: "mg" },
  { value: "bpc157", nameKey: "bpc157", unit: "mcg" },
];

// Standard titration schedules - use translation keys
const TITRATION_SCHEDULES = [
  { peptideKey: "retatrutide", weekRangeKey: "weeks1_4", doseKey: "reta_1mg" },
  { peptideKey: "retatrutide", weekRangeKey: "weeks5_8", doseKey: "reta_2mg" },
  { peptideKey: "retatrutide", weekRangeKey: "weeks9plus", doseKey: "reta_3mg" },
  { peptideKey: "tirzepatide", weekRangeKey: "weeks1_4", doseKey: "tirz_2_5mg" },
  { peptideKey: "tirzepatide", weekRangeKey: "weeks5_8", doseKey: "tirz_5mg" },
  { peptideKey: "tirzepatide", weekRangeKey: "weeks9plus", doseKey: "tirz_10mg" },
  { peptideKey: "ipamorelin", weekRangeKey: "daily", doseKey: "ipa_200mcg" },
  { peptideKey: "tesamorelin", weekRangeKey: "daily", doseKey: "tesa_1_28mg" },
  { peptideKey: "nad", weekRangeKey: "scim", doseKey: "nad_20_50mg" },
  { peptideKey: "klow", weekRangeKey: "daily", doseKey: "klow_0_5mg" },
  { peptideKey: "glow", weekRangeKey: "daily", doseKey: "glow_0_5mg" },
  { peptideKey: "ss31", weekRangeKey: "daily", doseKey: "ss31_5_10mg" },
  { peptideKey: "motsc", weekRangeKey: "perWeek2_3", doseKey: "motsc_5mg" },
  { peptideKey: "tb500", weekRangeKey: "weeks1_4", doseKey: "tb500_2mg_twice" },
  { peptideKey: "tb500", weekRangeKey: "maintenance", doseKey: "tb500_2mg_monthly" },
  { peptideKey: "ghkcu", weekRangeKey: "weekly", doseKey: "ghkcu_1mg" },
  { peptideKey: "bpc157", weekRangeKey: "weeks1_4", doseKey: "bpc157_250mcg" },
];

export const SchedulePlannerPage = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = getDateLocale(i18n.language);
  
  const [peptide, setPeptide] = useState("");
  const [doseAmount, setDoseAmount] = useState("");
  const [injectionsPerWeek, setInjectionsPerWeek] = useState("1");
  const [durationWeeks, setDurationWeeks] = useState("4");
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [schedule, setSchedule] = useState([]);
  const [copied, setCopied] = useState(false);

  // Get translated peptide label
  const getPeptideLabel = (nameKey) => {
    return t(`schedule.titrationData.${nameKey}`);
  };

  const selectedPeptide = useMemo(() => {
    return PEPTIDE_OPTIONS.find((p) => p.value === peptide);
  }, [peptide]);

  const selectedPeptideLabel = useMemo(() => {
    if (selectedPeptide?.nameKey) {
      return getPeptideLabel(selectedPeptide.nameKey);
    }
    return peptide;
  }, [selectedPeptide, i18n.language]);

  const generateSchedule = () => {
    if (!peptide || !doseAmount || !startDate) return;

    const start = new Date(startDate);
    const weeks = parseInt(durationWeeks);
    const injectionsPerWeekNum = parseInt(injectionsPerWeek);
    const newSchedule = [];

    // Calculate days between injections
    const daysBetween = Math.floor(7 / injectionsPerWeekNum);

    let currentDate = start;
    let injectionNumber = 1;

    for (let week = 0; week < weeks; week++) {
      for (let inj = 0; inj < injectionsPerWeekNum; inj++) {
        newSchedule.push({
          number: injectionNumber,
          date: new Date(currentDate),
          dose: `${doseAmount} ${selectedPeptide?.unit || "mg"}`,
          peptide: selectedPeptideLabel,
        });
        injectionNumber++;
        currentDate = addDays(currentDate, daysBetween);
      }
      // Ensure we start the next week on the right day
      currentDate = addWeeks(start, week + 1);
    }

    setSchedule(newSchedule);
  };

  const formatScheduleText = () => {
    if (schedule.length === 0) return "";

    let text = `${selectedPeptideLabel} ${t('schedule.yourSchedule')}\n`;
    text += `${t('schedule.dose')}: ${doseAmount} ${selectedPeptide?.unit}\n`;
    text += `${t('schedule.frequency')}: ${injectionsPerWeek}x ${t('schedule.timesPerWeek', { count: parseInt(injectionsPerWeek) })}\n`;
    text += `${t('schedule.duration')}: ${t('schedule.weeks', { count: parseInt(durationWeeks) })}\n\n`;

    schedule.forEach((item) => {
      text += `#${item.number} - ${format(item.date, "EEEE, MMMM d, yyyy", { locale: dateLocale })} - ${item.dose}\n`;
    });

    return text;
  };

  const copyToClipboard = async () => {
    const text = formatScheduleText();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadICS = () => {
    if (schedule.length === 0) return;

    let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Lumon Peptides//Schedule Planner//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
`;

    schedule.forEach((item, index) => {
      const dateStr = format(item.date, "yyyyMMdd");
      const uid = `peptide-${peptide}-${index}-${Date.now()}@lumonpeptides.com`;
      
      icsContent += `BEGIN:VEVENT
UID:${uid}
DTSTAMP:${format(new Date(), "yyyyMMdd'T'HHmmss'Z'")}
DTSTART;VALUE=DATE:${dateStr}
SUMMARY:${item.peptide} Injection #${item.number}
DESCRIPTION:Dose: ${item.dose}
END:VEVENT
`;
    });

    icsContent += `END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${peptide}-schedule.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Format frequency option text
  const getFrequencyText = (num) => {
    if (num === 1) return t('schedule.timesPerWeek', { count: 1 });
    return t('schedule.timesPerWeek_plural', { count: num });
  };

  // Format duration option text  
  const getDurationText = (num) => {
    if (num === 1) return t('schedule.weeks', { count: 1 });
    return t('schedule.weeks_plural', { count: num });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Breadcrumb Navigation */}
      <PageBreadcrumb currentPath="/schedule" />

      {/* Page Header */}
      <div className="w-full py-8 px-4 border-b border-border/50 bg-gradient-to-b from-muted/30 to-background">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-primary rounded-lg">
              <CalendarDays className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {t('schedule.title')}
            </h1>
          </div>
          <p className="text-muted-foreground max-w-2xl">
            {t('schedule.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* How to Use */}
        <Card className="mb-8 bg-primary/5 border-primary/20">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  {t('schedule.howToUseTitle')}
                </h3>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>{t('schedule.howToUseSteps.step1')}</li>
                  <li>{t('schedule.howToUseSteps.step2')}</li>
                  <li>{t('schedule.howToUseSteps.step3')}</li>
                  <li>{t('schedule.howToUseSteps.step4')}</li>
                  <li>{t('schedule.howToUseSteps.step5')}</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Planner Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Syringe className="w-5 h-5 text-primary" />
                {t('schedule.scheduleSettingsTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Peptide Selection */}
              <div className="space-y-2">
                <Label>{t('schedule.selectPeptide')}</Label>
                <Select value={peptide} onValueChange={setPeptide}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder={t('schedule.choosePeptide')} />
                  </SelectTrigger>
                  <SelectContent>
                    {PEPTIDE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {t(`schedule.titrationData.${opt.nameKey}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dose Amount */}
              <div className="space-y-2">
                <Label>{t('schedule.dosePerInjection')} ({selectedPeptide?.unit || "mg"})</Label>
                <Input
                  type="number"
                  placeholder={t('common.enterDoseAmount')}
                  value={doseAmount}
                  onChange={(e) => setDoseAmount(e.target.value)}
                  className="h-11"
                  step="0.1"
                  min="0"
                />
              </div>

              {/* Injections Per Week */}
              <div className="space-y-2">
                <Label>{t('schedule.frequency')}</Label>
                <Select value={injectionsPerWeek} onValueChange={setInjectionsPerWeek}>
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {getFrequencyText(num)}
                        {num === 7 && ` (${t('common.daily')})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label>{t('schedule.duration')}</Label>
                <Select value={durationWeeks} onValueChange={setDurationWeeks}>
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 6, 8, 10, 12, 16, 20, 24].map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {getDurationText(num)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              <div className="space-y-2">
                <Label>{t('schedule.startDate')}</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-11"
                />
              </div>

              <Button 
                onClick={generateSchedule} 
                className="w-full h-11 gap-2"
                disabled={!peptide || !doseAmount}
              >
                <Calendar className="w-4 h-4" />
                {t('schedule.generateSchedule')}
              </Button>
            </CardContent>
          </Card>

          {/* Generated Schedule */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                {t('schedule.yourSchedule')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {schedule.length > 0 ? (
                <>
                  <div className="flex gap-2 mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyToClipboard}
                      className="gap-2"
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                      {copied ? t('common.copied') : t('schedule.copySchedule')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadICS}
                      className="gap-2"
                    >
                      <Download className="w-4 h-4" />
                      {t('schedule.downloadCalendar')}
                    </Button>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-[400px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>{t('schedule.date')}</TableHead>
                            <TableHead>{t('schedule.dose')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {schedule.map((item) => (
                            <TableRow key={item.number}>
                              <TableCell className="font-medium">{item.number}</TableCell>
                              <TableCell>
                                {format(item.date, "EEE, MMM d, yyyy", { locale: dateLocale })}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="bg-primary/5">
                                  {item.dose}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    {t('common.total')}: {schedule.length} {t('common.injections')} / {getDurationText(parseInt(durationWeeks))}
                  </p>
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>
                    {t('common.fillFormPrompt')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Separator className="my-10" />

        {/* Standard Titration Schedules */}
        <Card>
          <CardHeader>
            <CardTitle>{t('schedule.standardSchedules')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('schedule.scheduleTable.peptide')}</TableHead>
                    <TableHead>{t('common.weekRangeFrequency')}</TableHead>
                    <TableHead>{t('schedule.dose')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {TITRATION_SCHEDULES.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{t(`schedule.titrationData.${item.peptideKey}`)}</TableCell>
                      <TableCell>{t(`schedule.weekRanges.${item.weekRangeKey}`)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-muted/50">
                          {t(`schedule.doses.${item.doseKey}`)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 text-center">
              {t('common.generalGuidelines')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Page Navigation */}
      <PageNavigation currentPath="/schedule" />

      {/* Disclaimer Footer */}
      <div className="border-t border-border/50 bg-muted/20 py-6 px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <strong>{t('common.disclaimer')}</strong> {t('common.disclaimerText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SchedulePlannerPage;
